import { CheckCircle2, Circle, Clock, Phone, ClipboardCheck, Send, Eye, ThumbsUp, Wrench, Sparkles, MessageSquare } from "lucide-react";

/** How long between two ISO timestamps, in a friendly short form ("3m" / "2h 14m" / "—"). */
function dur(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "<1m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function fmt(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * Surfaces every milestone timestamp already captured on the job into a
 * single right-rail timeline. Shows how fast the workshop moved through
 * the journey — perfect for KPI conversations with manufacturers + a
 * delightful "look how slick we were" trophy for the customer-service
 * manager.
 */
export default function CustomerJourneyTimeline({ job }) {
  if (!job) return null;

  // Compose the steps. Each entry: { key, label, icon, doneAt, helper }
  const steps = [
    {
      key: "booked",
      label: "Booked in",
      icon: ClipboardCheck,
      doneAt: job.created_at,
      helper: job.appointment_at ? `For ${fmt(job.appointment_at)}` : null,
    },
    {
      key: "pre-call",
      label: "Pre-call sent",
      icon: Phone,
      doneAt: job.pre_call_sent_at || job.pre_call_sent_email_at || job.pre_call_sent_sms_at,
      channels: [
        job.pre_call_sent_email_at && "email",
        job.pre_call_sent_sms_at && "sms",
      ].filter(Boolean),
    },
    {
      key: "checked-in",
      label: "Checked in",
      icon: CheckCircle2,
      doneAt: job.checked_in_at,
      helper: job.welcome_sent_at ? "Welcome message dispatched" : null,
    },
    {
      key: "sent",
      label: "Quote sent",
      icon: Send,
      doneAt: job.sent_at || job.sent_email_at || job.sent_sms_at,
      channels: [
        job.sent_email_at && "email",
        job.sent_sms_at && "sms",
      ].filter(Boolean),
    },
    {
      key: "viewed",
      label: "Customer viewed",
      icon: Eye,
      doneAt: job.viewed_at,
      sinceSent: dur(job.sent_at, job.viewed_at),
    },
    {
      key: "responded",
      label: (() => {
        if (job.status === "approved") return "Customer approved";
        if (job.status === "rejected") return "Customer declined";
        if (job.status === "partial") return "Customer responded (partial)";
        if (job.status === "needs_advice") return "Asked for a callback";
        return "Customer responded";
      })(),
      icon: ThumbsUp,
      doneAt: job.responded_at,
      sinceSent: dur(job.sent_at, job.responded_at),
    },
    {
      key: "completed",
      label: "Job completed",
      icon: Wrench,
      doneAt: job.completed_at,
      sinceCheckedIn: dur(job.checked_in_at, job.completed_at),
    },
    {
      key: "thank-you",
      label: "Thank-you sent",
      icon: Sparkles,
      doneAt: job.thank_you_sent_at || job.thank_you_sent_email_at || job.thank_you_sent_sms_at,
      channels: [
        job.thank_you_sent_email_at && "email",
        job.thank_you_sent_sms_at && "sms",
      ].filter(Boolean),
    },
  ];

  // Total elapsed (booked → latest milestone)
  const lastDone = steps.filter((s) => s.doneAt).pop();
  const totalElapsed = dur(job.created_at, lastDone?.doneAt);

  return (
    <div className="card-default p-5" data-testid="customer-journey-timeline">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline">Customer journey</div>
          <h2 className="font-display font-bold text-lg tracking-tight mt-0.5">Timeline</h2>
        </div>
        {totalElapsed && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/15 text-primary text-[11px] font-bold uppercase tracking-wider"
            data-testid="journey-total-elapsed"
            title="Total time from booking to the latest milestone">
            <Clock className="w-3 h-3" /> {totalElapsed}
          </span>
        )}
      </div>

      <ol className="mt-4 relative pl-1">
        {steps.map((step, idx) => {
          const done = !!step.doneAt;
          const Icon = step.icon;
          const isLast = idx === steps.length - 1;
          return (
            <li
              key={step.key}
              className="flex gap-3 pb-4 relative"
              data-testid={`journey-step-${step.key}`}
              data-done={done ? "1" : "0"}
            >
              {!isLast && (
                <span
                  className={`absolute left-3 top-7 w-px h-[calc(100%-1.5rem)] ${done ? "bg-primary/40" : "bg-border"}`}
                  aria-hidden
                />
              )}
              <div className={`flex-shrink-0 w-6 h-6 rounded-full grid place-items-center border-2 ${
                done ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border text-muted-foreground"
              }`}>
                {done ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-sm font-semibold ${done ? "text-foreground" : "text-muted-foreground"}`}>
                    {step.label}
                  </span>
                  {done && step.doneAt && (
                    <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">{fmt(step.doneAt)}</span>
                  )}
                </div>
                {/* Sub-line: channels, helper text, or elapsed-since hints */}
                <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {step.helper && <span>{step.helper}</span>}
                  {step.channels && step.channels.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="w-2.5 h-2.5" /> {step.channels.join(" + ")}
                    </span>
                  )}
                  {step.sinceSent && done && (
                    <span className="text-primary font-semibold">{step.sinceSent} after the quote was sent</span>
                  )}
                  {step.sinceCheckedIn && done && (
                    <span className="text-primary font-semibold">{step.sinceCheckedIn} after check-in</span>
                  )}
                  {!done && (
                    <span className="italic">Pending</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {job.status === "completed" && totalElapsed && (
        <div className="mt-2 pt-3 border-t border-border text-[11px] text-muted-foreground text-center">
          🏁 Full journey completed in <strong className="text-foreground">{totalElapsed}</strong>
        </div>
      )}
    </div>
  );
}
